// ---------------------------------------------------------------------------
// DonutChart -- SVG Donut with Rounded Arc Caps
// ---------------------------------------------------------------------------
// Renders a donut chart using SVG circles with:
//   - stroke-linecap="round" for capsule arc ends
//   - Center metric (total, custom, or none)
//   - Sequential Felton palette colors
//   - CapsuleLegend integration
// ---------------------------------------------------------------------------

import React, { useMemo } from 'react';
import { getChartColorCss } from '../constants/chartColors';
import { CapsuleLegend } from '../primitives/CapsuleLegend';
import type { LegendItem } from '../primitives/CapsuleLegend';
import { ChartCard } from './ChartCard';
import { getPlatformIcon } from '../../shared/platformIcons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DonutDataItem {
  readonly label: string;
  readonly value: number;
}

export type CenterLabelMode = 'total' | 'custom' | 'none';

export interface DonutChartProps {
  /** Data segments */
  readonly data: ReadonlyArray<DonutDataItem>;
  /** Card title */
  readonly title: string;
  /** Optional card subtitle */
  readonly subtitle?: string;
  /** Center label mode */
  readonly centerLabel?: CenterLabelMode;
  /** Custom center text (used when centerLabel is "custom") */
  readonly centerText?: string;
  /** SVG size in pixels */
  readonly size?: number;
  /** Show legend below donut */
  readonly showLegend?: boolean;
  /** Color offset for palette variety across charts */
  readonly colorOffset?: number;
  /** Additional CSS class */
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// SVG constants
// ---------------------------------------------------------------------------

const STROKE_WIDTH = 18;
const GAP_DEGREES = 2; // Small gap between segments

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DonutChart: React.FC<DonutChartProps> = ({
  data,
  title,
  subtitle,
  centerLabel = 'total',
  centerText = '',
  size = 150,
  showLegend = true,
  colorOffset = 0,
  className,
}) => {
  const total = useMemo(() => data.reduce((sum, item) => sum + item.value, 0), [data]);

  const radius = (size - STROKE_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Compute segment offsets and lengths
  const segments = useMemo(() => {
    if (total === 0) return [];

    const totalGap = data.length * GAP_DEGREES;
    const availableDegrees = 360 - totalGap;
    let cumulativeOffset = 0;

    return data.map((item, index) => {
      const fraction = item.value / total;
      const segmentDegrees = fraction * availableDegrees;
      const dashLength = (segmentDegrees / 360) * circumference;
      const gapLength = circumference - dashLength;

      // Offset includes accumulated segments + gaps
      const offset = -(cumulativeOffset / 360) * circumference;
      cumulativeOffset += segmentDegrees + GAP_DEGREES;

      return {
        label: item.label,
        value: item.value,
        colorIndex: index + colorOffset,
        dashArray: `${dashLength} ${gapLength}`,
        dashOffset: offset,
        color: getChartColorCss(index + colorOffset),
      };
    });
  }, [data, total, circumference]);

  // Center display text (compact formatting for large numbers)
  const displayCenter = useMemo(() => {
    if (centerLabel === 'none') return null;
    if (centerLabel === 'custom') return centerText;
    if (total >= 10000) {
      return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(total);
    }
    return total.toLocaleString('en-US');
  }, [centerLabel, centerText, total]);

  // Legend items
  const legendItems: ReadonlyArray<LegendItem> = useMemo(
    () =>
      data.map((item, index) => ({
        label: item.label,
        value: item.value,
        colorIndex: index + colorOffset,
      })),
    [data, colorOffset]
  );

  const legend = showLegend ? <CapsuleLegend items={legendItems} showPercentage /> : undefined;

  // Empty state when no data
  if (data.length === 0 || total === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle} className={className}>
        <div className="flex items-center justify-center min-h-[120px] text-v11-text-muted text-sm">
          No data available for this period
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title={title} subtitle={subtitle} className={className}>
      <div className="flex items-center justify-center gap-6">
        {/* Donut SVG */}
        <div className="flex-shrink-0">
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="overflow-visible"
          >
            {/* Background ring */}
            <circle
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke="hsl(var(--v11-bg-secondary))"
              strokeWidth={STROKE_WIDTH}
            />

            {/* Data segments */}
            {segments.map((segment, index) => (
              <circle
                key={`${segment.label}-${index}`}
                data-testid="donut-segment"
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={STROKE_WIDTH}
                strokeDasharray={segment.dashArray}
                strokeDashoffset={segment.dashOffset}
                strokeLinecap="round"
                transform={`rotate(-90 ${center} ${center})`}
              />
            ))}

            {/* Center metric */}
            {displayCenter !== null && (
              <text
                x={center}
                y={center}
                textAnchor="middle"
                dominantBaseline="central"
                className="fill-v11-text-primary font-display text-2xl font-bold"
              >
                {displayCenter}
              </text>
            )}
          </svg>
        </div>

        {/* Inline legend (right side) */}
        {showLegend && (
          <div className="flex flex-col gap-2 min-w-0">
            {data.map((item, index) => {
              const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
              return (
                <div key={`${item.label}-${index}`} className="flex items-center gap-2">
                  <span
                    data-testid="legend-pill"
                    className="h-2 w-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: getChartColorCss(index + colorOffset) }}
                  />
                  <span className="text-xs text-v11-text-secondary truncate">{item.label}</span>
                  <span className="text-xs text-v11-text-muted tabular-nums ml-auto flex-shrink-0">
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ChartCard>
  );
};
